import { useEffect, useMemo, useState } from 'react';
import { useToolStore } from '@/store';
import useComposerText from '@/hooks/useComposerText';
import { matchToolsForIntent, MIN_QUERY_LENGTH } from '@/constants/utils/tool_intent_matcher';

const DEBOUNCE_MS = 220;

/**
 * Debounced tool-intent matches for the live composer text.
 * Shared by ToolIntentSuggestions and ChatView padding logic.
 */
export default function useToolIntentSuggestions(contextId) {
  const message = useComposerText(contextId);
  const toolsBySlug = useToolStore((state) => state.toolsBySlug);
  const [debouncedMessage, setDebouncedMessage] = useState('');

  useEffect(() => {
    const handle = setTimeout(() => setDebouncedMessage(message || ''), DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [message]);

  const suggestions = useMemo(() => {
    const trimmed = (debouncedMessage || '').trim();
    if (trimmed.length < MIN_QUERY_LENGTH) return [];
    return matchToolsForIntent(debouncedMessage, Object.values(toolsBySlug));
  }, [debouncedMessage, toolsBySlug]);

  return {
    suggestions,
    hasSuggestions: suggestions.length > 0,
    message,
    debouncedMessage,
  };
}
